import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';

import { TREAD_DOCS_URL } from '~constants/consts';

// Matches IntercomLauncher's BUTTON_SIZE, so both columns fill the same global nav row height.
const BUTTON_HEIGHT = '64px';

const HelpCenterLauncher = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isPopoverOpen = Boolean(anchorEl);

  return (
    <>
      <Box
        component="button"
        aria-label={t('app_header.help_center_label')}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: BUTTON_HEIGHT,
          px: 2,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: isPopoverOpen
            ? theme.brandV2.colors.treadBlack
            : theme.brandV2.colors.treadGray3,

          '&:hover': {
            backgroundColor: theme.semantic.actionHover,
          },
        }}
      >
        {/* 24px matches the account-menu ExpandMore icon size in AppHeader */}
        <HelpOutlineOutlinedIcon sx={{ fontSize: '24px' }} />
        {/* Hidden below md so the button stays icon-only and fits the mobile header */}
        <Typography
          sx={{
            display: { xs: 'none', md: 'block' },
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          {t('app_header.help_center_label')}
        </Typography>
      </Box>

      <Popover
        id="help-center-popover"
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // 280px fits the title, description, and CTA button without wrapping
        slotProps={{ paper: { sx: { width: 280, p: 2.5 } } }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
          {t('app_header.help_center_label')}
        </Typography>
        <Typography
          sx={{
            fontSize: '13px',
            color: theme.brandV2.colors.treadGray2,
            lineHeight: 1.5,
            mb: 2,
          }}
        >
          {t('app_header.help_center_description')}
        </Typography>
        <Box
          component="a"
          href={TREAD_DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // 36px matches the standard action-button height used elsewhere in the app
            height: '36px',
            borderRadius: theme.brandV2.borderRadius,
            backgroundColor: theme.palette.primary.main,
            color: theme.semantic.textOnBrand,
            fontSize: '14px',
            fontWeight: 500,
            textDecoration: 'none',

            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          {t('app_header.help_center_visit_docs')}
        </Box>
      </Popover>
    </>
  );
};

export default HelpCenterLauncher;
